import { useState } from 'react'
import { AlertTriangle, ChevronRight, Database, Plus, ShieldCheck, Undo2 } from 'lucide-react'
import { ConfirmDialog, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useBiGrantMutations, useBiGrants, useBiMetadata } from '../api'
import type { BiGrantView } from '../types'
import { Select } from '@/components/ui/select'

interface LegalEntityOption { id: string; name: string }

/**
 * Who may read the reporting schema, and what there is to read.
 *
 * <p>Creating the database login is deliberately absent, and the screen says so rather than
 * leaving somebody hunting for the button. The application has no privilege to create
 * database roles and should not be given one — a web request that can mint logins is a much
 * larger thing than this feature is worth.
 */
export function BiAccessPage() {
  useBreadcrumb()
  const metadata = useBiMetadata()
  const grants = useBiGrants()
  const entities = useLookup<LegalEntityOption>('legalEntities')
  const { grant, revoke } = useBiGrantMutations()

  const [draft, setDraft] = useState<{ dbRoleName: string; legalEntityId: string; description: string } | null>(null)
  const [toRevoke, setToRevoke] = useState<BiGrantView | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const knownRoles = metadata.data?.reportingRoles ?? []

  async function onGrant() {
    if (!draft) return
    setError(null)
    try {
      await grant.mutateAsync({
        dbRoleName: draft.dbRoleName,
        legalEntityId: draft.legalEntityId || null,
        description: draft.description || null,
      })
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That grant could not be written')
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Reporting access"
        subtitle="Power BI and anything else that reads the database directly."
        actions={
          <Button
            type="button"
            disabled={knownRoles.length === 0}
            onClick={() => {
              setError(null)
              setDraft({ dbRoleName: knownRoles[0] ?? '', legalEntityId: '', description: '' })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            Grant access
          </Button>
        }
      />

      <Card className="space-y-2 p-5">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <ShieldCheck className="size-4" aria-hidden="true" />
          How the filtering works
        </h2>
        <p className="text-sm text-slate-700">
          Every view in the <code>bi</code> schema filters on the grants below, keyed on the
          database login doing the reading. A reporting connection cannot widen what it sees by
          setting anything in its connection string — there is no company or entity column to
          filter on, and setting one changes nothing.
        </p>
        {metadata.isSuccess && (
          <ol className="ms-5 list-decimal space-y-1 text-sm text-slate-700">
            {metadata.data.howToConnect.map((step) => <li key={step}>{step}</li>)}
          </ol>
        )}
      </Card>

      <Card className="p-5">
        <h2 className="mb-1 flex items-center gap-2 text-base font-semibold">
          <Database className="size-4" aria-hidden="true" />
          Views in the <code>{metadata.data?.schema ?? 'bi'}</code> schema
        </h2>
        <p className="mb-3 text-sm text-slate-600">
          Read from the database itself, so this cannot describe a schema that is a migration
          out of date.
        </p>
        {metadata.isPending && <Skeleton className="h-32" />}
        {metadata.isError && (
          <ErrorState error={metadata.error} onRetry={() => void metadata.refetch()} />
        )}
        <ul className="divide-y divide-slate-100">
          {metadata.data?.views.map((view) => (
            <li key={view.name}>
              <button
                type="button"
                className="flex w-full items-center gap-2 py-2 text-start"
                aria-expanded={expanded === view.name}
                onClick={() => setExpanded(expanded === view.name ? null : view.name)}
              >
                <ChevronRight
                  className={`size-4 shrink-0 transition-transform ${expanded === view.name ? 'rotate-90' : ''}`}
                  aria-hidden="true"
                />
                <span className="font-mono text-sm">{view.name}</span>
                <span className="text-sm text-slate-500">
                  {view.columns.length} column{view.columns.length === 1 ? '' : 's'}
                </span>
              </button>
              {expanded === view.name && (
                <div className="pb-3 ps-6">
                  {view.comment && <p className="mb-2 text-sm text-slate-600">{view.comment}</p>}
                  <table className="text-sm">
                    <tbody>
                      {view.columns.map((column) => (
                        <tr key={column.name}>
                          <td className="py-0.5 pe-4 font-mono">{column.name}</td>
                          <td className="py-0.5 text-slate-600">{column.type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <div className="border-b border-slate-200 p-4">
          <h2 className="text-base font-semibold">Grants</h2>
          {knownRoles.length === 0 ? (
            <p className="mt-1 flex items-start gap-2 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              No reporting logins exist yet. A database administrator creates one with
              <code className="mx-1">db/roles/create-bi-role.sql</code>
              before access can be granted here — the application has no privilege to create
              database roles, deliberately.
            </p>
          ) : (
            <p className="mt-1 text-sm text-slate-600">
              Logins available: {knownRoles.map((role) => <code key={role} className="me-2">{role}</code>)}
            </p>
          )}
        </div>

        {grants.isPending && <Skeleton className="m-4 h-24" />}
        {grants.isError && <ErrorState error={grants.error} onRetry={() => void grants.refetch()} />}
        {grants.isSuccess && grants.data.length === 0 && (
          <EmptyState
            title="Nothing is granted"
            description="A reporting login with no grant sees no rows at all — the views fail closed."
          />
        )}
        {grants.isSuccess && grants.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-start text-slate-600">
                <tr>
                  <th className="p-3 font-medium">Login</th>
                  <th className="p-3 font-medium">Reads</th>
                  <th className="p-3 font-medium">Why</th>
                  <th className="p-3 font-medium">Granted</th>
                  <th className="p-3 font-medium">State</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {grants.data.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100">
                    <td className="p-3 font-mono">{row.dbRoleName}</td>
                    <td className="p-3">
                      {row.legalEntityId
                        ? entities.data?.find((entity) => entity.id === row.legalEntityId)?.name
                          ?? 'One entity'
                        : 'Every entity in the company'}
                    </td>
                    <td className="p-3 text-slate-600">{row.description ?? '—'}</td>
                    <td className="p-3 text-slate-600">{row.grantedAt.slice(0, 10)}</td>
                    <td className="p-3">
                      {row.revokedAt ? (
                        <StatusBadge status="REVOKED" tone="neutral" label="Revoked" />
                      ) : !row.roleExists ? (
                        <StatusBadge status="MISSING" tone="danger" label="Login is gone" />
                      ) : (
                        <StatusBadge status="ACTIVE" tone="success" label="Active" />
                      )}
                    </td>
                    <td className="p-3 text-end">
                      {!row.revokedAt && (
                        <Button type="button" variant="outline" size="sm" onClick={() => setToRevoke(row)}>
                          <Undo2 className="size-4" aria-hidden="true" />
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        title="Grant reporting access"
        description="The login must already exist in the database."
        submitLabel="Grant it"
        loading={grant.isPending}
        error={error}
        onSubmit={() => void onGrant()}
      >
        {draft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="grant-role">Reporting login</Label>
              <Select
                id="grant-role"
                className="w-full"
                value={draft.dbRoleName}
                onChange={(event) => setDraft({ ...draft, dbRoleName: event.target.value })}
              >
                {knownRoles.map((role) => <option key={role} value={role}>{role}</option>)}
              </Select>
            </div>
            <div>
              <Label htmlFor="grant-entity">May read</Label>
              <Select
                id="grant-entity"
                className="w-full"
                value={draft.legalEntityId}
                onChange={(event) => setDraft({ ...draft, legalEntityId: event.target.value })}
              >
                <option value="">Every entity in the company</option>
                {entities.data?.map((entity) => (
                  <option key={entity.id} value={entity.id}>{entity.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="grant-why">Why</Label>
              <Input
                id="grant-why"
                value={draft.description}
                placeholder="Group finance headcount dashboard"
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <ConfirmDialog
        open={toRevoke != null}
        onOpenChange={(open) => { if (!open) setToRevoke(null) }}
        title="Revoke this grant?"
        description={
          toRevoke
            ? `${toRevoke.dbRoleName} stops seeing these rows at its next query. The grant is kept as a record of what it could once read.`
            : ''
        }
        confirmLabel="Revoke"
        onConfirm={() => {
          if (toRevoke) revoke.mutate(toRevoke.id)
          setToRevoke(null)
        }}
      />
    </div>
  )
}
